<template>
  <div v-if="hasRight(user, 'dashboard', 'view')" class="realtime-kpi-strip">
    <b-row class="kpi-strip-row align-items-start">
        <b-col cols="6" sm="6" md="4" lg="2" class="kpi-strip-col"
        @click="handleKpiClick('new_leads')">
          <kpi-card
            title="New Leads (no follow-up yet)"
            :value="kpiData.new_leads.count"
            :trend="kpiData.new_leads.trend"
            color="blue darken-1"
          />
        </b-col>
        <b-col cols="6" sm="6" md="4" lg="2" class="kpi-strip-col"
        @click="handleKpiClick('total_calls')">
          <kpi-card
            title="Total Calls"
            :value="totalCallsMainDisplay"
            :trend="totalCallsMainTrend"
            :subtitle="totalCallsVolumeSubtitle"
            color="green darken-1"
          />
        </b-col>
        <b-col cols="6" sm="6" md="4" lg="2" class="kpi-strip-col"
        @click="handleKpiClick('site_visits')">
          <kpi-card
            title="Site Visits"
            :value="kpiData.site_visits.count"
            :trend="kpiData.site_visits.trend"
            color="orange darken-1"
          />
        </b-col>
        <b-col cols="6" sm="6" md="4" lg="2" class="kpi-strip-col"
        @click="handleKpiClick('closures')">
          <kpi-card
            title="Closures"
            :value="kpiData.closures.count"
            :trend="kpiData.closures.count_trend"
            color="purple darken-1"
          />
        </b-col>
        <b-col cols="6" sm="6" md="4" lg="2" class="kpi-strip-col"
        @click="handleKpiClick('not_connected_calls')">
          <kpi-card
            title="Not Connected Call"
            :value="kpiData.not_connected_calls.count"
            :trend="kpiData.not_connected_calls.trend"
            color="red darken-1"
          />
        </b-col>
        <b-col cols="6" sm="6" md="4" lg="2" class="kpi-strip-col"
        @click="handleKpiClick('priority_leads')">
          <kpi-card
            title="Priority Leads (Hot / Warm / Cold)"
            :value="kpiData.priority_leads.count"
            :trend="kpiData.priority_leads.trend"
            :subtitle="priorityLeadsSubtitle"
            color="blue-grey darken-1"
          />
        </b-col>
        <b-col cols="6" sm="6" md="4" lg="2" class="kpi-strip-col"
        @click="handleKpiClick('lost_leads')">
          <kpi-card
            title="Lost"
            :value="kpiData.lost_leads.count"
            :trend="kpiData.lost_leads.trend"
            color="grey darken-3"
          />
        </b-col>
        <b-col cols="6" sm="6" md="4" lg="2" class="kpi-strip-col"
        @click="handleKpiClick('transfer_to_sales')">
          <kpi-card
            title="Transfer to Sales"
            :value="kpiData.transfer_to_sales.count"
            :trend="kpiData.transfer_to_sales.trend"
            color="teal darken-1"
          />
        </b-col>
      </b-row>
  </div>
</template>

<script>
import KpiCard from './KpiCard.vue';
import { hasRight } from '@/utils/auth';
import { dashboardGet } from './dashboardApi';

import { BRow, BCol } from 'bootstrap-vue';

const emptyRealtimeKpi = () => ({
  new_leads: { count: 0, trend: null },
  total_calls: { count: 0, trend: null, lead_count: 0, lead_count_trend: null },
  site_visits: { count: 0, trend: null },
  closures: { count: 0, value: 0, count_trend: null, value_trend: null },
  not_connected_calls: { count: 0, trend: null },
  priority_leads: { count: 0, trend: null, hot: 0, warm: 0, cold: 0 },
  lost_leads: { count: 0, trend: null },
  transfer_to_sales: { count: 0, trend: null },
});

export default {
  name: 'RealtimeKpiStrip',
  emits: ['kpi-card-click'],
  components: {
    KpiCard,
    BRow,
    BCol,
  },
  props: {
    filters: {
      type: Object,
      required: true,
    },
    user: {
      type: Object,
      default: null,
    },
    dashboardData: {
      type: Object,
      default: null,
    },
    useAggregate: { type: Boolean, default: false },
    baseApi: {
      type: String,
      default: () => process.env.VUE_APP_APIENDPOINT || '',
    },
    accessToken: {
      type: String,
      default: () => localStorage.getItem('accessToken') || '',
    },
  },
  data() {
    return {
      kpiData: emptyRealtimeKpi(),
      pollingInterval: null,
    };
  },
  watch: {
    dashboardData: {
      deep: true,
      handler(val) {
        if (val && val.realtime_kpi != null && hasRight(this.user, 'dashboard', 'view')) {
          this.kpiData = { ...emptyRealtimeKpi(), ...val.realtime_kpi };
        }
      },
    },
    filters: {
      deep: true,
      immediate: true,
      handler() {
        if (!hasRight(this.user, 'dashboard', 'view')) return;
        if (this.useAggregate) {
          if (this.dashboardData && this.dashboardData.realtime_kpi != null) {
            this.kpiData = { ...emptyRealtimeKpi(), ...this.dashboardData.realtime_kpi };
          }
          return;
        }
        if (this.dashboardData && this.dashboardData.realtime_kpi != null) {
          this.kpiData = { ...emptyRealtimeKpi(), ...this.dashboardData.realtime_kpi };
          return;
        }
        this.fetchKpiData();
      },
    },
    user: {
      deep: true,
      immediate: true,
      handler(newVal) {
        if (hasRight(newVal, 'dashboard', 'view')) {
          if (this.useAggregate) {
            if (this.dashboardData && this.dashboardData.realtime_kpi != null) {
              this.kpiData = { ...emptyRealtimeKpi(), ...this.dashboardData.realtime_kpi };
            }
            return;
          }
          this.startPolling();
          this.fetchKpiData();
        } else {
          this.stopPolling();
          this.kpiData = emptyRealtimeKpi();
        }
      },
    },
  },
  beforeDestroy() {
    this.stopPolling();
  },
  computed: {
    /** Main KPI number: distinct leads with ≥1 call; falls back to call count if API has no lead_count. */
    totalCallsMainDisplay() {
      const tc = this.kpiData.total_calls;
      if (!tc) return 0;
      const lc = tc.lead_count;
      if (lc !== undefined && lc !== null && lc !== '') {
        const n = Number(lc);
        if (!Number.isNaN(n)) return n;
      }
      const c = Number(tc.count);
      return Number.isNaN(c) ? 0 : c;
    },
    /** Trend vs yesterday for the same metric shown in the main value. */
    totalCallsMainTrend() {
      const tc = this.kpiData.total_calls;
      if (!tc) return null;
      if (tc.lead_count !== undefined && tc.lead_count !== null) {
        const t = tc.lead_count_trend;
        return t !== undefined && t !== null ? t : null;
      }
      return tc.trend != null ? tc.trend : null;
    },
    /** Secondary line: total call follow-up rows (includes repeat calls per lead). */
    totalCallsVolumeSubtitle() {
      const tc = this.kpiData.total_calls;
      if (!tc || tc.count == null || tc.count === '') return null;
      if (tc.lead_count == null || tc.lead_count === '') return null;
      const calls = Number(tc.count);
      if (Number.isNaN(calls)) return null;
      const w = calls === 1 ? 'call' : 'calls';
      return `${calls.toLocaleString()} total ${w} logged`;
    },
    priorityLeadsSubtitle() {
      const p = this.kpiData.priority_leads;
      if (!p) return null;
      const h = Number(p.hot) || 0;
      const w = Number(p.warm) || 0;
      const c = Number(p.cold) || 0;
      return `Hot ${h} · Warm ${w} · Cold ${c}`;
    },
  },
  methods: {
    hasRight, // Make the helper available in the template
    async fetchKpiData() {
      if (!hasRight(this.user, 'dashboard', 'view')) return;
      if (this.useAggregate || (this.dashboardData && this.dashboardData.realtime_kpi != null)) {
        if (this.dashboardData && this.dashboardData.realtime_kpi != null) {
          this.kpiData = { ...emptyRealtimeKpi(), ...this.dashboardData.realtime_kpi };
        }
        return;
      }
      try {
        const response = await dashboardGet(this.baseApi, this.accessToken, '/dashboard/crm/realtime-kpi', this.filters);
        this.kpiData = { ...emptyRealtimeKpi(), ...response.data.data };
      } catch (error) {
        console.error('Error fetching real-time KPI data:', error);
      }
    },
    startPolling() {
      if (this.useAggregate) return;
      this.pollingInterval = setInterval(this.fetchKpiData, 30000);
    },
    stopPolling() {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    },
    handleKpiClick(kpiKey) {
      this.$emit('kpi-card-click', kpiKey);
    },
  },
};
</script>

<style scoped>
/* Match PresalesPipeline: no inner portlet-body (avoids CrmManagementDashboard .portlet-body 14px + extra padding) */
.realtime-kpi-strip {
  padding: 8px 12px 16px;
}

/* Natural card heights — KpiCard uses w-100 for column width */
.kpi-strip-row {
  margin-left: -6px;
  margin-right: -6px;
}

.kpi-strip-col {
  padding-left: 6px;
  padding-right: 6px;
}
</style>
